import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { ProjetoFeiraService } from '../../services/projeto-feira.service';
import { FiltrarProjeto } from '../../models/project-filter.model';
import { ProjectsDto } from '../../models/project-response.dto';
import { Projeto } from '../../models/project.model';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import { PaginationDto } from 'src/app/core/dto/pagination.dto';
import { ProjetoFormModalComponent } from '../projeto-form-modal/projeto-form-modal.component';
import { AtualizarStatusProjetoComponent } from '../../../clients/components/status-projeto/atualizar-status-projeto/atualizar-status-projeto.component';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { ProjectService } from '../../../clients/services/project.service';
import { Projeto as ProjetoCliente } from '../../../clients/dto/project';

@Component({
  selector: 'app-projeto-list',
  templateUrl: './projeto-list.component.html',
  styleUrls: ['./projeto-list.component.scss'],
  standalone: false
})
export class ProjetoListComponent implements OnInit, OnDestroy {
  projetos: Projeto[] = [];
  filtroForm: FormGroup;
  private readonly destroy$ = new Subject<void>();
  isLoading = false;
  error = '';
  erro403 = false;
  private atualizandoPaginaProgramaticamente = false;
  pagination: PaginationDto = {
    paginaAtual: 1,
    quantidadePorPagina: 10,
    totalDeItens: 0,
    ultimaPagina: 1
  };

  // Permissões
  podeVer = false;
  podeEditar = false;

  constructor(
    private projetoFeiraService: ProjetoFeiraService,
    private projectService: ProjectService,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private toastService: ToastService,
    private router: Router,
    private authService: AuthService
  ) {
    this.filtroForm = this.fb.group({
      PaginaAtual: [1],
      QuantidadePorPagina: [10],
      Cliente: [''],
      Descricao: [''],
      Feira: [''],
      Status: [null] // null = Todos os status por padrão
    });
  }

  ngOnInit(): void {
    this.verificarPermissoes();
    this.configurarFiltros();
    if (this.podeVer) {
      this.carregarProjetos();
    }
  }

  verificarPermissoes(): void {
    this.podeVer = this.authService.verificarPermissao('Prj_Realizados', 'Ver');
    this.podeEditar = this.authService.verificarPermissao('Prj_Realizados', 'Editar');
    
    // Se não tiver permissão para ver, definir erro 403
    if (!this.podeVer) {
      this.erro403 = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  carregarProjetos(): void {
    // Verificar permissão antes de carregar
    if (!this.podeVer) {
      return;
    }

    const formValue = this.filtroForm.value;
    // Verificar se a API espera 0-based ou 1-based
    // Por enquanto, vamos testar enviando 1-based diretamente
    const paginaAtual = Math.max(1, formValue.PaginaAtual || 1); // Garantir mínimo 1
    
    const filtros: any = {
      PaginaAtual: paginaAtual.toString(), // Enviar 1-based diretamente
      QuantidadePorPagina: formValue.QuantidadePorPagina?.toString() || '10',
      Cliente: formValue.Cliente || '',
      Descricao: formValue.Descricao || '',
      Feira: formValue.Feira || ''
    };

    // Adicionar Status apenas se tiver valor (não null/undefined)
    // Se for null, não envia o parâmetro (endpoint retorna todos os status)
    if (formValue.Status !== null && formValue.Status !== undefined && formValue.Status !== '') {
      filtros.Status = formValue.Status.toString();
    }

    this.getProjects(filtros);
  }

  getProjects(filtros: any): void {
    this.isLoading = true;
    this.error = '';

    this.projetoFeiraService
      .consultarProjetosEmAndamento(filtros)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ProjectsDto) => {
          this.projetos = response.projetos || [];
          // API retorna paginaAtual como 1-based (mesmo formato do ngb-pagination)
          const novaPagina = Math.max(1, response.paginacao?.paginaAtual ?? 1);
          
          // Marcar que estamos atualizando programaticamente para evitar loop
          this.atualizandoPaginaProgramaticamente = true;
          
          // Atualizar paginação (ngb-pagination com [(page)] vai sincronizar automaticamente)
          this.pagination = {
            paginaAtual: novaPagina,
            quantidadePorPagina: response.paginacao?.quantidadePorPagina || 10,
            totalDeItens: response.paginacao?.totalDeItens || 0,
            ultimaPagina: Math.max(1, response.paginacao?.ultimaPagina || 1)
          };
          
          // Sincronizar formulário sem disparar eventos
          if (this.filtroForm.get('PaginaAtual')?.value !== novaPagina) {
            this.filtroForm.patchValue({ PaginaAtual: novaPagina }, { emitEvent: false });
          }
          
          this.isLoading = false;
          
          // Resetar flag após um pequeno delay
          setTimeout(() => {
            this.atualizandoPaginaProgramaticamente = false;
          }, 100);
        },
        error: (error) => {
          if (error.status === 403) {
            this.erro403 = true;
            this.isLoading = false;
            return;
          }
          console.error('Erro ao carregar projetos:', error);
          this.error = 'Erro ao carregar dados dos projetos.';
          this.toastService.show('Erro ao consultar lista de projetos', {
            classname: 'bg-danger text-light',
          });
          this.isLoading = false;
        }
      });
  }

  private ultimaPaginaForm: number = 1;
  private ultimosFiltros: any = {};

  configurarFiltros(): void {
    // Não configurar filtros se não tiver permissão para ver
    if (!this.podeVer) {
      return;
    }

    this.ultimaPaginaForm = this.filtroForm.get('PaginaAtual')?.value || 1;
    this.ultimosFiltros = { ...this.filtroForm.value };
    
    this.filtroForm.valueChanges
      .pipe(
        debounceTime(400),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => {
        // Verificar permissão antes de processar mudanças
        if (!this.podeVer) {
          return;
        }

        const paginaAtual = value.PaginaAtual || 1;
        const paginaAnterior = this.ultimaPaginaForm;
        
        // Verificar se algum filtro (exceto PaginaAtual) mudou
        const filtrosMudaram = 
          value.Cliente !== this.ultimosFiltros.Cliente ||
          value.Descricao !== this.ultimosFiltros.Descricao ||
          value.Feira !== this.ultimosFiltros.Feira ||
          value.Status !== this.ultimosFiltros.Status ||
          value.QuantidadePorPagina !== this.ultimosFiltros.QuantidadePorPagina;
        
        // Se apenas PaginaAtual mudou e não veio do onPageChange, pode ser do valueChanges
        // Nesse caso, se outros filtros não mudaram, não recarregar (evitar duplicação)
        if (!filtrosMudaram && paginaAtual === paginaAnterior) {
          // Nada mudou, não recarregar
          return;
        }
        
        // Se outros filtros mudaram (não apenas PaginaAtual), resetar para página 1
        if (filtrosMudaram) {
          this.filtroForm.patchValue({ PaginaAtual: 1 }, { emitEvent: false });
          this.ultimaPaginaForm = 1;
        } else {
          this.ultimaPaginaForm = paginaAtual;
        }
        
        // Atualizar últimos filtros
        this.ultimosFiltros = { ...value };
        
        this.carregarProjetos();
      });
  }

  limparFiltros(): void {
    this.ultimaPaginaForm = 1;
    this.filtroForm.reset({
      PaginaAtual: 1,
      QuantidadePorPagina: 10,
      Cliente: '',
      Descricao: '',
      Feira: '',
      Status: null // Todos os status
    });
    this.ultimosFiltros = { ...this.filtroForm.value };
  }

  onPageChange(page: number): void {
    // Se estamos atualizando programaticamente, ignorar este evento
    if (this.atualizandoPaginaProgramaticamente) {
      return;
    }
    
    // ngb-pagination já retorna 1-based, que é o que o formulário espera
    const paginaAtual = Math.max(1, page);
    this.ultimaPaginaForm = paginaAtual;
    this.ultimosFiltros = { ...this.filtroForm.value, PaginaAtual: paginaAtual };
    this.filtroForm.patchValue({
      PaginaAtual: paginaAtual
    }, { emitEvent: false });
    this.carregarProjetos();
  }

  onQuantidadePorPaginaChange(): void {
    this.ultimaPaginaForm = 1;
    this.ultimosFiltros = { ...this.filtroForm.value, PaginaAtual: 1, QuantidadePorPagina: this.filtroForm.get('QuantidadePorPagina')?.value };
    this.filtroForm.patchValue({
      PaginaAtual: 1
    }, { emitEvent: false });
    this.carregarProjetos();
  }

  novoProjeto(): void {
    const modalRef = this.modalService.open(ProjetoFormModalComponent, {
      size: 'md',
      backdrop: 'static'
    });

    modalRef.result.then(
      (result) => {
        if (result === 'created') {
          this.carregarProjetos();
        }
      },
      (dismissed) => {
        // Modal foi fechado sem ação
      }
    );
  }

  getStatusClass(status: string | number | undefined): string {
    if (status === undefined || status === null) return 'badge bg-secondary';
    
    const statusNum = typeof status === 'number' ? status : parseInt(status.toString());
    
    switch (statusNum) {
      case 0: // IniciadoProjeto
        return 'badge bg-info';
      case 1: // FechadoAprovado
        return 'badge bg-success';
      case 2: // FechadoRecusado
        return 'badge bg-danger';
      case 3: // IniciadoENegociando
        return 'badge bg-warning';
      case 4: // FinalizadoComSucesso
        return 'badge bg-primary';
      default:
        return 'badge bg-secondary';
    }
  }

  getStatusText(status: string | number | undefined): string {
    if (status === undefined || status === null) return '-';
    
    const statusNum = typeof status === 'number' ? status : parseInt(status.toString());
    
    switch (statusNum) {
      case 0:
        return 'Iniciado';
      case 1:
        return 'Aprovado';
      case 2:
        return 'Recusado';
      case 3:
        return 'Em Negociação';
      case 4:
        return 'Finalizado';
      default:
        return `Status ${statusNum}`;
    }
  }

  formatarMedida(valor: number | undefined | null): string {
    if (valor === undefined || valor === null || valor === 0) {
      return '-';
    }
    return valor.toFixed(2).replace('.', ',');
  }

  onClickCliente(projeto: Projeto): void {
    if (projeto.clienteId) {
      this.router.navigate([`clientes/${projeto.clienteId}`], { queryParams: { extra: 2 } });
    }
  }

  podeAtualizarStatus(status: string | number | undefined): boolean {
    if (status === undefined || status === null) return true;
    
    const statusNum = typeof status === 'number' ? status : parseInt(status.toString());
    
    // Status que NÃO permitem atualização: Aprovado (1), Recusado (2), Finalizado (4)
    // Status que permitem atualização: Iniciado (0), Em Negociação (3)
    return statusNum === 0 || statusNum === 3;
  }

  // modalAtualizarStatus(event: Projeto): void {
  //   // Validação de segurança: prevenir atualização se o status não permitir
  //   const statusAtual = event.statusProjeto?.statusProjeto;
  //   if (!this.podeAtualizarStatus(statusAtual)) {
  //     this.toastService.show('Não é possível atualizar o status de projetos Aprovados, Recusados ou Finalizados.', {
  //       classname: 'bg-warning text-light',
  //     });
  //     return;
  //   }

  //   const modalRef = this.modalService.open(AtualizarStatusProjetoComponent);
  //   modalRef.componentInstance.projetoId = event.id;
  //   modalRef.componentInstance.statusAtual = statusAtual;
    
  //   modalRef.closed.subscribe({
  //     next: () => {
  //       // Recarregar a lista de projetos para refletir o novo status
  //       this.carregarProjetos();
  //     },
  //     error: (error) => {
  //       console.error(error);
  //     }
  //   });
  // }

  editarProjeto(projeto: Projeto): void {
    // Buscar dados completos do projeto para edição
    this.projectService.getProjectById(projeto.id).subscribe({
      next: (projetoCompleto: ProjetoCliente) => {
        // Adicionar clienteId do projeto da lista (não vem na resposta da API)
        if (projeto.clienteId) {
          projetoCompleto.clienteId = projeto.clienteId;
        }

        const modalRef = this.modalService.open(ProjetoFormModalComponent, {
          size: 'md',
          backdrop: 'static'
        });

        modalRef.componentInstance.event = projetoCompleto;
        if (projeto.clienteId) {
          modalRef.componentInstance.clienteId = projeto.clienteId;
        }

        modalRef.closed.subscribe({
          next: (result) => {
            // Recarregar a lista de projetos após edição
            if (result === 'updated') {
              this.carregarProjetos();
            }
          },
          error: (error) => {
            console.error(error);
          }
        });
      },
      error: (error) => {
        console.error('Erro ao buscar dados do projeto:', error);
        this.toastService.show('Erro ao carregar dados do projeto para edição', {
          classname: 'bg-danger text-light',
        });
      }
    });
  }
}

